import type { FC } from 'react'
import styled from '@emotion/styled'

import ButtonText from '@segment/matcha/components/ButtonText'

import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import Divider from '@segment/matcha/components/Divider'

import Typography from '@segment/matcha/components/Typography'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import type { LogoTrackProps } from '@segment/matcha/recipes/LogoCarousel/LogoTrack'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import LogoCarousel from '../LogoCarousel'

import PlanFeaturesImage from './PlanFeaturesImage'

const copy = {
  heading: `Your free plan includes:`,
  description: [
    `Data collection from 2 sources of your choice`,
    `Unlimited data distribution`,
    `Up to 1,000 monthly users`,
    `500,000 Reverse ETL records per month`,
    `1 data warehouse`,
    `450+ pre-built integrations`,
    `Developer tools and debugger`,
    `Data and privacy controls`,
    `10 seats`,
  ],
  linkText: `Compare Segment pricing plans`,
  linkHref: `/pricing/`,
}

export interface PlanFeaturesProps {
  logos: LogoTrackProps[`icons`]
}
const PlanFeatures: FC<PlanFeaturesProps> = ({ logos }) => {
  const pageConfig = usePageConfig()
  return (
    <SectionBaseWrapper variant="noCopy">
      <Section.Contents>
        <StyledSectionBase variant="2Col">
          <Section.Copy>
            <CopyTextBase>
              <CopyText.Heading variant="h2" tag="h1">
                {copy.heading}
              </CopyText.Heading>
              <CopyText.Description>
                <List>
                  {copy.description.map((desc, idx) => (
                    <StyledListItem key={`desc-${idx}`}>
                      <Benefit variant="pLarge">{desc}</Benefit>
                    </StyledListItem>
                  ))}
                </List>
              </CopyText.Description>
              <ButtonText href={getAbsoluteUrl(copy.linkHref, pageConfig)} iconEnd={ChevronRightIcon}>
                {copy.linkText}
              </ButtonText>
            </CopyTextBase>
          </Section.Copy>
          <Section.Contents>
            <PlanFeaturesImage />
          </Section.Contents>
        </StyledSectionBase>
        <StyledLogoCarousel logos={logos} />
        <Divider />
      </Section.Contents>
    </SectionBaseWrapper>
  )
}

export default PlanFeatures

const SectionBaseWrapper = styled(SectionBase)`
  ${Section.Wrapper} {
    padding-top: 0;
    ${({ theme }) => theme.breakpoints.up(`md`)} {
      padding-bottom: 0;
    }
  }
`

const StyledSectionBase = styled(SectionBase)`
  ${Section.Wrapper} {
    ${({ theme }) => theme.breakpoints.up(`md`)} {
      gap: 80px;
      padding-top: 120px;
      padding-bottom: 80px;
    }
    ${({ theme }) => theme.breakpoints.down(`xs`)} {
      padding: 0 0 40px;
    }
  }
`

const List = styled.ul`
  margin-bottom: 24px;
  padding-left: 16px;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    margin-bottom: 0;
  }
`

const StyledListItem = styled.li`
  list-style-image: url("data:image/svg+xml;charset=UTF-8,%3csvg width='12' height='9' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath fill-rule='evenodd' clip-rule='evenodd' d='M11.045.667a.91.91 0 0 1 .025 1.305L5.031 8.126a.95.95 0 0 1-1.356 0L.93 5.33a.91.91 0 0 1 .025-1.305.954.954 0 0 1 1.331.025l2.067 2.106L9.714.692a.954.954 0 0 1 1.33-.025Z' fill='%23121C2D'/%3e%3c/svg%3e ");

  font-weight: 400;
  padding: 0 0 8px 8px;
`

const Benefit = styled(Typography)`
  margin: 0;
  display: flex;
  gap: 8px;
`

const StyledLogoCarousel = styled(LogoCarousel)`
  padding-bottom: 60px;
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    padding-bottom: 120px;
  }
`
